import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Calendar,
  Building2,
  Users,
  FileSpreadsheet,
  FileText,
  Share2,
  RotateCcw,
  Search,
  Wallet,
  Truck,
  HardHat,
  Receipt,
  TrendingDown,
  TrendingUp,
  ArrowRight,
  Filter,
  CheckCircle2,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import { useSession } from '../lib/session';
import { useProjectPickerList } from '../lib/project-report';
import {
  useDailyReportData,
  type DailyReportFilters,
  type DailyReportItem,
} from '../lib/daily-report';
import {
  exportDailyReportExcel,
  exportDailyReportPdf,
  shareDailyReportPdf,
} from '../lib/daily-export';
import { canShareFiles } from '../platform';
import Shell from '../components/Shell';
import {
  Select,
  Field,
  TableSkeleton,
  ErrorState,
  EmptyState,
  formatDate,
  useToast,
  StatusChip,
} from '../components/ui';

export default function DailyReport() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const actor = useSession((s) => s.actor);

  const { data: picker, isLoading: pickerLoading } = useProjectPickerList();

  const [clientId, setClientId] = useState(() => searchParams.get('clientId') ?? '');
  const [projectId, setProjectId] = useState(() => searchParams.get('projectId') ?? '');
  const [fromDate, setFromDate] = useState(() => searchParams.get('from') ?? '');
  const [toDate, setToDate] = useState(() => searchParams.get('to') ?? '');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tableSearch, setTableSearch] = useState<string>('');
  const [busy, setBusy] = useState<'excel' | 'pdf' | 'share' | null>(null);

  // Derive unique clients list from projects
  const clients = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of picker ?? []) {
      if (!map.has(p.clientId)) map.set(p.clientId, p.clientName);
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [picker]);

  // Projects filtered by selected client
  const availableProjects = useMemo(() => {
    return (picker ?? []).filter((p) => !clientId || p.clientId === clientId);
  }, [picker, clientId]);

  // Auto-select first project if only one exists and none selected
  const activeProjectId = projectId || (availableProjects.length === 1 ? availableProjects[0]?.id : '');

  const queryFilters: DailyReportFilters = useMemo(
    () => ({
      clientId: clientId || undefined,
      projectId: activeProjectId || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
    [clientId, activeProjectId, fromDate, toDate],
  );

  const { data, isLoading, error, refetch } = useDailyReportData(queryFilters, !!activeProjectId);

  // Quick Date Preset Helpers
  function setPreset(days: number | 'thisMonth' | 'all') {
    if (days === 'all') {
      setFromDate('');
      setToDate('');
      return;
    }

    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    setToDate(todayStr);

    if (days === 'thisMonth') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
        .toISOString()
        .slice(0, 10);
      setFromDate(firstDay);
    } else {
      const prior = new Date();
      prior.setDate(today.getDate() - days);
      setFromDate(prior.toISOString().slice(0, 10));
    }
  }

  function handleReset() {
    setClientId('');
    setProjectId('');
    setFromDate('');
    setToDate('');
    setTypeFilter('all');
    setTableSearch('');
  }

  // Calculate category counts for filter tabs
  const categoryCounts = useMemo(() => {
    const counts = { all: 0, payment: 0, material: 0, labour: 0, expense: 0 };
    if (!data?.items) return counts;
    counts.all = data.items.length;
    for (const item of data.items) {
      if (item.type in counts) {
        counts[item.type as keyof typeof counts]++;
      }
    }
    return counts;
  }, [data?.items]);

  // Filter items in the table
  const displayedItems = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((item) => {
      const matchesType = typeFilter === 'all' || item.type === typeFilter;
      const matchesSearch =
        !tableSearch.trim() ||
        item.particulars.toLowerCase().includes(tableSearch.toLowerCase()) ||
        item.mode.toLowerCase().includes(tableSearch.toLowerCase()) ||
        item.typeLabel.toLowerCase().includes(tableSearch.toLowerCase()) ||
        item.date.includes(tableSearch);
      return matchesType && matchesSearch;
    });
  }, [data?.items, typeFilter, tableSearch]);

  async function handleExportExcel() {
    if (!data) return;
    setBusy('excel');
    try {
      await exportDailyReportExcel(data, 'VNS CONSTRUCTION');
      toast.show('Client Day Book Excel exported successfully');
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not export Excel', true);
    } finally {
      setBusy(null);
    }
  }

  async function handleExportPdf() {
    if (!data) return;
    setBusy('pdf');
    try {
      await exportDailyReportPdf(data, 'VNS CONSTRUCTION');
      toast.show('Client Day Book PDF exported successfully');
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not export PDF', true);
    } finally {
      setBusy(null);
    }
  }

  async function handleSharePdf() {
    if (!data) return;
    setBusy('share');
    try {
      await shareDailyReportPdf(data, 'VNS CONSTRUCTION');
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not share PDF', true);
    } finally {
      setBusy(null);
    }
  }

  const summary = data?.summary;
  const isFiltered = !!clientId || !!activeProjectId || !!fromDate || !!toDate;

  return (
    <Shell title="Client Day Book — Daily Project Report" crumb="Reports">
      {/* Back and Header Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <button
          className="btn btn-auto btn-sm btn-ghost"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--text-soft)',
            paddingLeft: 4,
          }}
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft size={14} />
          <span>Back to Reports</span>
        </button>

        {data && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn-auto btn-sm"
              onClick={() => void handleExportExcel()}
              disabled={busy !== null}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <FileSpreadsheet size={14} style={{ color: '#059669' }} />
              <span>{busy === 'excel' ? 'Exporting…' : 'Export Excel'}</span>
            </button>

            <button
              className="btn btn-auto btn-sm btn-primary"
              onClick={() => void handleExportPdf()}
              disabled={busy !== null}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <FileText size={14} />
              <span>{busy === 'pdf' ? 'Generating…' : 'Export PDF'}</span>
            </button>

            {canShareFiles() && (
              <button
                className="btn btn-auto btn-sm btn-ghost"
                onClick={() => void handleSharePdf()}
                disabled={busy !== null}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Share2 size={14} />
                <span>Share</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Filter Card */}
      <div
        className="card"
        style={{
          padding: '16px 20px',
          marginBottom: 20,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Filter size={15} style={{ color: 'var(--accent)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-faint)' }}>
              Report Filters & Date Range
            </span>
          </div>

          {isFiltered && (
            <button
              type="button"
              className="btn btn-auto btn-sm btn-ghost"
              onClick={handleReset}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
            >
              <RotateCcw size={12} />
              <span>Reset Filters</span>
            </button>
          )}
        </div>

        {/* Filter Inputs Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 14,
            alignItems: 'flex-end',
          }}
        >
          {/* Client Selector */}
          <Field label="Filter by Client (Optional)">
            <Select
              value={clientId}
              onChange={(val) => {
                setClientId(val);
                setProjectId('');
              }}
              placeholder="All Clients"
              options={[
                { value: '', label: 'All Clients' },
                ...clients.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </Field>

          {/* Project Selector (Required) */}
          <Field label="Select Project (Required)">
            <Select
              value={activeProjectId}
              onChange={(val) => setProjectId(val)}
              placeholder={pickerLoading ? 'Loading projects…' : 'Choose a project…'}
              options={[
                { value: '', label: 'Choose a project…' },
                ...availableProjects.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.clientName})`,
                })),
              ]}
            />
          </Field>

          {/* Start Date Picker */}
          <Field label="From Date">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Field>

          {/* End Date Picker */}
          <Field label="To Date">
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Field>
        </div>

        {/* Quick Date Range Preset Buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 14,
            flexWrap: 'wrap',
            paddingTop: 12,
            borderTop: '1px dashed var(--border)',
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-faint)', marginRight: 4 }}>
            Quick Range:
          </span>
          {[
            { id: 'all', label: 'All Time', active: !fromDate && !toDate },
            {
              id: 7,
              label: 'Last 7 Days',
              active:
                toDate === new Date().toISOString().slice(0, 10) &&
                fromDate ===
                  new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
            },
            {
              id: 14,
              label: 'Last 14 Days',
              active:
                toDate === new Date().toISOString().slice(0, 10) &&
                fromDate ===
                  new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10),
            },
            {
              id: 30,
              label: 'Last 30 Days',
              active:
                toDate === new Date().toISOString().slice(0, 10) &&
                fromDate ===
                  new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
            },
            {
              id: 'thisMonth',
              label: 'This Month',
              active:
                toDate === new Date().toISOString().slice(0, 10) &&
                fromDate ===
                  new Date(new Date().getFullYear(), new Date().getMonth(), 1)
                    .toISOString()
                    .slice(0, 10),
            },
          ].map((preset) => (
            <button
              key={String(preset.id)}
              type="button"
              onClick={() => setPreset(preset.id as number | 'thisMonth' | 'all')}
              style={{
                fontSize: 11.5,
                fontWeight: preset.active ? 600 : 500,
                padding: '4px 10px',
                borderRadius: 14,
                border: preset.active
                  ? '1px solid #2563eb'
                  : '1px solid var(--border)',
                background: preset.active
                  ? 'rgba(37, 99, 235, 0.1)'
                  : 'var(--surface)',
                color: preset.active ? '#2563eb' : 'var(--text-soft)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      {!activeProjectId ? (
        <div className="card" style={{ padding: '48px 20px', textAlign: 'center' }}>
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              background: 'rgba(37, 99, 235, 0.08)',
              color: 'var(--brand)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 14,
            }}
          >
            <Building2 size={28} />
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px', color: 'var(--text)' }}>
            Choose a Project to View Daily Day Book
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-soft)', maxWidth: 460, margin: '0 auto 20px' }}>
            Select a project from the dropdown above to audit all client receipts, material costs, subcontractor wages, and daily site expenses chronologically.
          </p>

          {/* Quick Select Buttons from available projects */}
          {availableProjects.length > 0 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', maxWidth: 650, margin: '0 auto' }}>
              {availableProjects.slice(0, 6).map((p) => (
                <button
                  key={p.id}
                  className="btn btn-auto btn-sm btn-ghost"
                  onClick={() => setProjectId(p.id)}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--surface)',
                  }}
                >
                  <Building2 size={13} style={{ color: 'var(--accent)' }} />
                  <span>{p.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : isLoading ? (
        <div className="card" style={{ padding: 24 }}>
          <TableSkeleton rows={8} cols={5} />
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 24 }}>
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      ) : !summary ? (
        <div className="card">
          <EmptyState icon="filetext" title="No records found" />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Project Banner & Scope info */}
          <div
            className="card"
            style={{
              padding: '16px 20px',
              marginBottom: 20,
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#0369a1' }}>
                  {summary.projectName}
                </h2>
                <StatusChip value={summary.status} />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  marginTop: 6,
                  fontSize: 12.5,
                  color: '#075985',
                  flexWrap: 'wrap',
                }}
              >
                <span><strong>Client:</strong> {summary.clientName}</span>
                <span>•</span>
                <span><strong>Location:</strong> {summary.location}</span>
                <span>•</span>
                <span>
                  <strong>Date Range:</strong> {formatDate(summary.fromDate)} to {formatDate(summary.toDate)}
                </span>
              </div>
            </div>
          </div>



          {/* Table Container */}
          <div className="card" style={{ overflow: 'hidden' }}>
            {/* Table Control Bar */}
            <div
              style={{
                padding: '12px 18px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flex: '1 1 320px',
                  minWidth: 240,
                  flexWrap: 'wrap',
                }}
              >
                <div className="field-inline" style={{ width: '100%', maxWidth: 300, minWidth: 180, flex: '1 1 180px' }}>
                  <Search size={14} style={{ color: 'var(--text-faint)' }} />
                  <input
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    placeholder="Search daily entries by particulars, mode, date..."
                    aria-label="Search daily report entries"
                    style={{ fontSize: 13 }}
                  />
                </div>

                {/* Type Filter Buttons */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: '#ffffff',
                    padding: '4px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    boxShadow: 'var(--shadow-xs)',
                    overflowX: 'auto',
                    maxWidth: '100%',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {[
                    { key: 'all', label: 'All', count: categoryCounts.all },
                    { key: 'payment', label: 'Receipts', count: categoryCounts.payment },
                    { key: 'material', label: 'Materials', count: categoryCounts.material },
                    { key: 'labour', label: 'Labour', count: categoryCounts.labour },
                    { key: 'expense', label: 'Expenses', count: categoryCounts.expense },
                  ].map((t) => {
                    const isActive = typeFilter === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setTypeFilter(t.key)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 12px',
                          fontSize: 12.5,
                          fontWeight: isActive ? 700 : 500,
                          border: 'none',
                          borderRadius: 'var(--radius-xs)',
                          cursor: 'pointer',
                          background: isActive ? '#2563eb' : 'transparent',
                          color: isActive ? '#ffffff' : 'var(--text)',
                          boxShadow: isActive ? '0 1px 3px rgba(37, 99, 235, 0.3)' : 'none',
                          transition: 'all 0.15s ease',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        <span>{t.label}</span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '1px 7px',
                            borderRadius: 12,
                            background: isActive
                              ? 'rgba(255, 255, 255, 0.28)'
                              : '#f1f5f9',
                            color: isActive ? '#ffffff' : '#64748b',
                            minWidth: 18,
                            textAlign: 'center',
                          }}
                        >
                          {t.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ fontSize: 12.5, color: 'var(--text-soft)', flexShrink: 0 }}>
                Showing <strong>{displayedItems.length}</strong> of <strong>{summary.transactionCount}</strong> entries
              </div>
            </div>

            {/* Day Book Table */}
            <div className="table-wrap">
              {displayedItems.length === 0 ? (
                <div style={{ padding: '36px 20px', textAlign: 'center' }}>
                  <EmptyState
                    icon="filetext"
                    title={tableSearch ? 'No matching entries found' : 'No entries in this date range'}
                    hint="Try adjusting your search terms or expanding the date window."
                  />
                </div>
              ) : (
                <table style={{ minWidth: 840 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center', width: 55 }}>S.No</th>
                      <th style={{ textAlign: 'left', width: 105 }}>Date</th>
                      <th style={{ textAlign: 'center', width: 90 }}>Type</th>
                      <th style={{ textAlign: 'center', width: 90 }}>Mode</th>
                      <th style={{ textAlign: 'left' }}>Particulars and Remarks</th>
                      <th style={{ textAlign: 'right', width: 135 }}>Receipt Amount (₹)</th>
                      <th style={{ textAlign: 'right', width: 135 }}>Payable Amount (₹)</th>
                      <th style={{ textAlign: 'right', width: 145 }}>Running Balance (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening Balance Row if prior balance exists */}
                    {summary.openingBalancePaise !== 0 && (
                      <tr style={{ background: '#f8fafc', fontStyle: 'italic' }}>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--text-soft)' }}>
                          1
                        </td>
                        <td style={{ textAlign: 'left', fontWeight: 600 }}>
                          {formatDate(summary.fromDate)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: '#e2e8f0',
                              color: '#475569',
                              textTransform: 'uppercase',
                            }}
                          >
                            Opening
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', color: 'var(--text-faint)' }}>
                          —
                        </td>
                        <td style={{ textAlign: 'left', fontWeight: 600, color: 'var(--text-soft)' }}>
                          Opening Balance (Accumulated balance prior to {formatDate(summary.fromDate)})
                        </td>
                        <td className="num" style={{ textAlign: 'right', color: '#059669', fontWeight: 600 }}>
                          {summary.openingBalancePaise > 0 ? formatMoney(summary.openingBalancePaise) : '—'}
                        </td>
                        <td className="num" style={{ textAlign: 'right', color: '#ea580c', fontWeight: 600 }}>
                          {summary.openingBalancePaise < 0 ? formatMoney(Math.abs(summary.openingBalancePaise)) : '—'}
                        </td>
                        <td
                          className="num"
                          style={{
                            textAlign: 'right',
                            fontWeight: 700,
                            color: summary.openingBalancePaise >= 0 ? '#059669' : '#ea580c',
                          }}
                        >
                          {formatMoney(summary.openingBalancePaise)}
                        </td>
                      </tr>
                    )}

                    {/* Transaction Items */}
                    {displayedItems.map((row, idx) => {
                      const isReceipt = row.receiptPaise > 0;
                      const isPayable = row.payablePaise > 0;
                      const sNo = idx + (summary.openingBalancePaise !== 0 ? 2 : 1);

                      return (
                        <tr key={row.id}>
                          <td style={{ textAlign: 'center', color: 'var(--text-soft)' }}>
                            {sNo}
                          </td>
                          <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                            {formatDate(row.date)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                padding: '2px 7px',
                                borderRadius: 4,
                                textTransform: 'uppercase',
                                background:
                                  row.type === 'payment'
                                    ? '#ecfdf5'
                                    : row.type === 'material'
                                    ? '#eff6ff'
                                    : row.type === 'labour'
                                    ? '#f5f3ff'
                                    : '#fff7ed',
                                color:
                                  row.type === 'payment'
                                    ? '#059669'
                                    : row.type === 'material'
                                    ? '#2563eb'
                                    : row.type === 'labour'
                                    ? '#7c3aed'
                                    : '#ea580c',
                                border: `1px solid ${
                                  row.type === 'payment'
                                    ? '#a7f3d0'
                                    : row.type === 'material'
                                    ? '#bfdbfe'
                                    : row.type === 'labour'
                                    ? '#ddd6fe'
                                    : '#fed7aa'
                                }`,
                              }}
                            >
                              {row.typeLabel}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: 12,
                                background: 'var(--surface-3, #f1f5f9)',
                                color: 'var(--text-soft, #475569)',
                                border: '1px solid var(--border, #e2e8f0)',
                                display: 'inline-block',
                              }}
                            >
                              {row.mode || '—'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'left', lineHeight: 1.4 }}>
                            <span style={{ color: 'var(--text)', fontWeight: 500 }}>
                              {row.particulars}
                            </span>
                          </td>
                          <td
                            className="num"
                            style={{
                              textAlign: 'right',
                              color: isReceipt ? '#059669' : 'var(--text-faint)',
                              fontWeight: isReceipt ? 600 : 400,
                            }}
                          >
                            {isReceipt ? formatMoney(row.receiptPaise) : '—'}
                          </td>
                          <td
                            className="num"
                            style={{
                              textAlign: 'right',
                              color: isPayable ? 'var(--text)' : 'var(--text-faint)',
                              fontWeight: isPayable ? 600 : 400,
                            }}
                          >
                            {isPayable ? formatMoney(row.payablePaise) : '—'}
                          </td>
                          <td
                            className="num"
                            style={{
                              textAlign: 'right',
                              fontWeight: 600,
                              color: row.runningBalancePaise >= 0 ? '#059669' : '#ea580c',
                            }}
                          >
                            {formatMoney(row.runningBalancePaise)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {/* Totals row */}
                    <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                      <td colSpan={5} style={{ textAlign: 'right', paddingRight: 20 }}>
                        TOTAL TRANSACTIONS
                      </td>
                      <td className="num" style={{ textAlign: 'right', color: '#059669', fontSize: 13.5 }}>
                        {formatMoney(summary.totalReceiptsPaise)}
                      </td>
                      <td className="num" style={{ textAlign: 'right', color: '#ea580c', fontSize: 13.5 }}>
                        {formatMoney(summary.totalPayablesPaise)}
                      </td>
                      <td className="num" style={{ textAlign: 'right', color: 'var(--text)' }}>
                        —
                      </td>
                    </tr>

                    {/* Closing Balance row */}
                    <tr style={{ background: '#f0f9ff', fontWeight: 800 }}>
                      <td colSpan={5} style={{ textAlign: 'right', paddingRight: 20, color: '#0369a1' }}>
                        {summary.closingBalancePaise >= 0 ? 'CLOSING CREDIT BALANCE (SURPLUS)' : 'CLOSING DEBIT BALANCE (PAYABLE)'}
                      </td>
                      <td colSpan={2} />
                      <td
                        className="num"
                        style={{
                          textAlign: 'right',
                          fontSize: 14,
                          color: summary.closingBalancePaise >= 0 ? '#059669' : '#ea580c',
                        }}
                      >
                        {formatMoney(Math.abs(summary.closingBalancePaise))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </Shell>
  );
}
